'use client'

import { useState, useEffect, useRef } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { getBranches } from '@/app/actions/github'
import { deployProjectAction, startProjectAction, getProjectDeployLog } from '@/app/actions/projects'
import { useProjectLogs, useSSEEvent } from '@/components/providers/sse-provider'
import { Loader2, Rocket, Play } from 'lucide-react'

type DialogMode = 'deploy' | 'start' | 'watch'

interface DeployDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    project: {
        name: string
        branch: string
        status: string
        envVars?: string
    }
    mode?: DialogMode
}

const modeConfig = {
    deploy: { title: 'Deploy', buttonText: 'Deploy', Icon: Rocket },
    start: { title: 'Start', buttonText: 'Start', Icon: Play },
    watch: { title: 'Deployment in progress', buttonText: '', Icon: Rocket },
}

export function DeployDialog({ open, onOpenChange, project, mode = 'deploy' }: DeployDialogProps) {
    const [branches, setBranches] = useState<string[]>([])
    const [loadingBranches, setLoadingBranches] = useState(false)
    const [selectedBranch, setSelectedBranch] = useState(project.branch)
    const [envVars, setEnvVars] = useState(project.envVars || '')
    const [deploying, setDeploying] = useState(false)
    const [hasStarted, setHasStarted] = useState(false)
    const logRef = useRef<HTMLDivElement>(null)

    const config = modeConfig[mode]
    const isWatchMode = mode === 'watch'
    const isStartMode = mode === 'start'

    // Use SSE for real-time log updates - no polling needed!
    const { log: sseLog, status: sseStatus, reset: resetLogs } = useProjectLogs(
        open && hasStarted ? project.name : null
    )

    // Extract repo name from project.name (format: owner/repo)
    const repoFullName = project.name
    const prevOpenRef = useRef(false)

    // Track when deployment finishes via SSE status from deploy_log
    useEffect(() => {
        if (sseStatus && sseStatus !== 'deploying' && deploying) {
            console.log('[DeployDialog] Status from deploy_log:', sseStatus)
            setDeploying(false)
        }
    }, [sseStatus, deploying])
    
    // Also listen to project_update directly as a backup
    useSSEEvent('project_update', (event) => {
        if (open && hasStarted && event.project === project.name) {
            console.log('[DeployDialog] Got project_update:', event.status)
            if (event.status && event.status !== 'deploying') {
                setDeploying(false)
            }
        }
    }, [open, hasStarted, project.name])

    // Reset state and fetch branches only when dialog opens
    useEffect(() => {
        // Only reset when dialog opens (was closed, now open)
        if (open && !prevOpenRef.current && repoFullName) {
            setSelectedBranch(project.branch)
            setEnvVars(project.envVars || '')
            resetLogs()
            
            if (isWatchMode) {
                // In watch mode, start watching immediately
                setHasStarted(true)
                setDeploying(true)
                // Fetch initial log state
                getProjectDeployLog(project.name)
                    .then(result => {
                        // SSE will handle subsequent updates
                        if (result.status !== 'deploying') {
                            setDeploying(false)
                        }
                    })
                    .catch(error => {
                        console.error('Failed to fetch initial deploy log:', error)
                        setDeploying(false)
                    })
            } else if (isStartMode) {
                // Start mode - no branch selection needed
                setHasStarted(false)
                setDeploying(false)
            } else {
                // Deploy mode, fetch branches
                setLoadingBranches(true)
                setHasStarted(false)
                setDeploying(false)
                
                getBranches(repoFullName)
                    .then(setBranches)
                    .finally(() => setLoadingBranches(false))
            }
        }
        prevOpenRef.current = open
    }, [open, repoFullName, project.branch, project.envVars, project.name, isWatchMode, isStartMode, resetLogs])

    // Auto-scroll to bottom when log updates
    useEffect(() => {
        if (logRef.current) {
            logRef.current.scrollTop = logRef.current.scrollHeight
        }
    }, [sseLog])

    const handleAction = async () => {
        setDeploying(true)
        setHasStarted(true)
        resetLogs()
        
        try {
            if (isStartMode) {
                await startProjectAction(project.name)
            } else {
                await deployProjectAction(project.name, selectedBranch, envVars)
            }
        } catch (error) {
            console.error('Action failed:', error)
            setDeploying(false)
        }
    }

    const ActionIcon = config.Icon

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
                <DialogHeader>
                    <DialogTitle>{config.title}: {project.name}</DialogTitle>
                </DialogHeader>

                <div className="space-y-4 flex-1 flex flex-col min-h-0">
                    {!isStartMode && !isWatchMode && (
                        <>
                            <div className="space-y-2">
                                <Label>Branch</Label>
                                <Select 
                                    value={selectedBranch} 
                                    onValueChange={setSelectedBranch}
                                    disabled={hasStarted}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder={loadingBranches ? "Loading branches..." : "Select branch"} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {branches.map((branch) => (
                                            <SelectItem key={branch} value={branch}>
                                                {branch}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            {!hasStarted && (
                                <div className="space-y-2">
                                    <Label>Environment Variables (.env format)</Label>
                                    <Textarea
                                        value={envVars}
                                        onChange={(e) => setEnvVars(e.target.value)}
                                        placeholder="KEY=VALUE"
                                        rows={4}
                                        className="font-mono text-sm"
                                    />
                                    <p className="text-xs text-muted-foreground">
                                        Only specified keys will be overridden in the repository .env
                                    </p>
                                </div>
                            )}
                        </>
                    )}

                    {isStartMode && !hasStarted && (
                        <p className="text-sm text-muted-foreground">
                            Start containers without rebuilding the image. Using branch: <strong>{project.branch}</strong>
                        </p>
                    )}

                    {hasStarted && (
                        <div className="flex flex-col">
                            <Label className="mb-2">Log</Label>
                            <div 
                                ref={logRef}
                                className="h-[300px] overflow-y-auto border rounded-md bg-zinc-950 p-4"
                            >
                                <pre className="text-xs text-zinc-300 font-mono whitespace-pre-wrap">
                                    {sseLog || 'Starting...'}
                                </pre>
                            </div>
                        </div>
                    )}

                    <div className="flex justify-end pt-2">
                        {deploying ? (
                            <Button disabled>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                {isStartMode ? 'Starting...' : 'Deploying...'}
                            </Button>
                        ) : hasStarted ? (
                            <Button onClick={() => onOpenChange(false)}>
                                Close
                            </Button>
                        ) : (
                            <Button 
                                onClick={handleAction} 
                                disabled={!isStartMode && !selectedBranch}
                            >
                                <ActionIcon className="mr-2 h-4 w-4" />
                                {config.buttonText}
                            </Button>
                        )}
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    )
}
